import { memo, useMemo } from 'react';

import {
  ChakraSimpleGrid,
  DatePickerInput,
  InputGroupV2,
  TagColor,
  TagProps,
  TextInputV2,
  UniversalSelect,
} from '@equip.health/ui';

import {
  EditMode,
  InquiryInfoValue,
} from '~/components/common/inquiry/drawer/types';
import {
  buildEquipSourceUpdate,
  buildUniversalSelectSearchTerm,
  equipSourceMenuOptionsMapper,
  getInquirerRoleCategoryCode,
  getSelectedUniversalSelectOption,
  inquiryEnumMenuOptionsMapper,
  isEquipSourceDetailRequired,
} from '~/lib/util/inquiry.util';
import {
  useGetHearAboutEquipSourcesQuery,
  useGetInformationGatheredSourcesQuery,
  useGetInquirerRolesQuery,
} from '~/store/services/inquiry';

import ClosedReasonDropdown from './ClosedReasonDropdown';
import InquiryDrawerLayout from './InquiryDrawerLayout';
import { useTreatmentPrograms } from '~/lib/hooks/useTreatmentPrograms';
import { useFlags } from 'launchdarkly-react-client-sdk';

const TOP_LEVEL_ID = 'inquiry-info';

export const channelMenuOptions: TagProps[] = [
  {
    color: TagColor.orange,
    id: 'direct-tag',
    value: 'Direct',
  },
  {
    color: TagColor.marigold,
    id: 'referral-tag',
    value: 'Referral',
  },
];

interface InquiryInfoProps {
  inquiryInfoValue: InquiryInfoValue;
  showRequiredFieldErrors: boolean;
  updateInquiryInfo: (info: Partial<InquiryInfoValue>) => void;
  editMode: EditMode;
}

const InquiryInfo = memo(
  ({
    inquiryInfoValue,
    showRequiredFieldErrors,
    updateInquiryInfo,
    editMode,
  }: InquiryInfoProps) => {
    const {
      closedReasonId,
      equipSourceId,
      informationSourceId,
      inquirerRole,
      inquiryDate,
      isClosed,
      targetTreatmentProgramName,
      tellUsMore,
    } = inquiryInfoValue;

    const inquirerRoleCode = getInquirerRoleCategoryCode(inquirerRole);
    const invalidInquirerRole = showRequiredFieldErrors && !inquirerRole;
    const invalidEquipSource = showRequiredFieldErrors && !equipSourceId;
    const invalidInformationSource =
      showRequiredFieldErrors && !informationSourceId;
    const invalidClosedReason =
      showRequiredFieldErrors && isClosed && !closedReasonId;

    const isMissingRequired =
      invalidInquirerRole ||
      invalidEquipSource ||
      invalidInformationSource ||
      invalidClosedReason;

    const { data: inquirerRoles } = useGetInquirerRolesQuery();
    const { data: hearAboutEquipSources } = useGetHearAboutEquipSourcesQuery();
    const { data: informationGatheredSources } =
      useGetInformationGatheredSourcesQuery();
    const { newEpisodePrograms } = useTreatmentPrograms();

    const {
      enableAdmittingTreatmentProgram: isAdmittingTreatmentProgramEnabled,
    } = useFlags<LDFlags>();

    const equipSourceOptions = useMemo<UniversalSelectOption[]>(() => {
      return equipSourceMenuOptionsMapper(
        hearAboutEquipSources,
        inquirerRoleCode,
      );
    }, [inquirerRoleCode, hearAboutEquipSources]);

    const informationSourceOptions = useMemo<UniversalSelectOption[]>(() => {
      return inquiryEnumMenuOptionsMapper(informationGatheredSources);
    }, [informationGatheredSources]);

    const inquirerRoleMenuOptions = useMemo<UniversalSelectOption[]>(() => {
      return (inquirerRoles ?? []).map((roleEnum) => ({
        label: roleEnum.name,
        searchData: buildUniversalSelectSearchTerm(roleEnum.name),
        value: roleEnum.name,
      }));
    }, [inquirerRoles]);

    const targetTreatmentProgramOptions = useMemo<
      UniversalSelectOption[]
    >(() => {
      return newEpisodePrograms.map((programName) => ({
        label: programName,
        searchData: programName,
        value: programName,
      }));
    }, [newEpisodePrograms]);

    const showTellUsMoreField = isEquipSourceDetailRequired(
      hearAboutEquipSources,
      equipSourceId,
    );

    const handleUpdateEquipSource = (newValue: string) => {
      const update = buildEquipSourceUpdate(
        hearAboutEquipSources,
        equipSourceId,
        newValue,
      );
      if (update) updateInquiryInfo(update);
    };

    return (
      <InquiryDrawerLayout
        header="Inquiry information"
        id={TOP_LEVEL_ID}
        isMissingFields={isMissingRequired}
      >
        <ChakraSimpleGrid columns={2} spacing="24px" width="100%">
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__inquirer-role`}
            label="Inquirer role"
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}__inquirer-role`}
              isDisabled={editMode == EditMode.EDIT}
              isInvalid={invalidInquirerRole}
              isSearchable
              isSingleSelect
              onChange={(selected: UniversalSelectOption) =>
                updateInquiryInfo({
                  inquirerRole: selected?.value,
                })
              }
              options={inquirerRoleMenuOptions}
              value={getSelectedUniversalSelectOption(
                inquirerRole,
                inquirerRoleMenuOptions,
              )}
            />
          </InputGroupV2>
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__how-hear`}
            label="How did you first hear about Equip?"
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}__how-hear`}
              isInvalid={invalidEquipSource}
              isSearchable
              isSingleSelect
              onChange={(selected: UniversalSelectOption) =>
                handleUpdateEquipSource(selected?.value)
              }
              options={equipSourceOptions}
              value={getSelectedUniversalSelectOption(
                equipSourceId,
                equipSourceOptions,
              )}
            />
          </InputGroupV2>
          {showTellUsMoreField && (
            <InputGroupV2
              id={`${TOP_LEVEL_ID}__tell-us-more`}
              key={equipSourceId}
              label="Add details (optional)"
            >
              <TextInputV2
                id={`${TOP_LEVEL_ID}__tell-us-more`}
                onChange={(value: string) =>
                  updateInquiryInfo({ tellUsMore: value })
                }
                value={tellUsMore}
              />
            </InputGroupV2>
          )}
          <InputGroupV2
            id={`${TOP_LEVEL_ID}__information-gather`}
            label="How was this information gathered?"
          >
            <UniversalSelect
              id={`${TOP_LEVEL_ID}__information-gather`}
              isInvalid={invalidInformationSource}
              isSearchable
              isSingleSelect
              onChange={(selected: UniversalSelectOption) =>
                updateInquiryInfo({
                  informationSourceId: selected?.value,
                })
              }
              options={informationSourceOptions}
              value={getSelectedUniversalSelectOption(
                informationSourceId,
                informationSourceOptions,
              )}
            />
          </InputGroupV2>
          <InputGroupV2 id={`${TOP_LEVEL_ID}__created`} label="Created">
            <DatePickerInput
              defaultDate={inquiryDate ?? new Date()}
              id={`${TOP_LEVEL_ID}__created`}
              maxDate={new Date()}
              onSubmit={(date: Date) =>
                updateInquiryInfo({
                  inquiryDate: date,
                })
              }
              showTimeInput
            />
          </InputGroupV2>
          {editMode == EditMode.EDIT && (
            <ClosedReasonDropdown
              id={TOP_LEVEL_ID}
              isDisabled={!isClosed}
              isInvalid={invalidClosedReason}
              onSelect={(val: string) =>
                updateInquiryInfo({ closedReasonId: val })
              }
              value={closedReasonId}
            />
          )}
          {isAdmittingTreatmentProgramEnabled && (
            <InputGroupV2
              id={`${TOP_LEVEL_ID}__target_treatment_program`}
              isRequired={false}
              label="Admitting program"
            >
              <UniversalSelect
                id={`${TOP_LEVEL_ID}__target_treatment_program`}
                isSearchable
                isSingleSelect
                onChange={(selected: UniversalSelectOption) =>
                  updateInquiryInfo({
                    targetTreatmentProgramName: selected?.value,
                  })
                }
                options={targetTreatmentProgramOptions}
                value={getSelectedUniversalSelectOption(
                  targetTreatmentProgramName,
                  targetTreatmentProgramOptions,
                )}
              />
            </InputGroupV2>
          )}
        </ChakraSimpleGrid>
      </InquiryDrawerLayout>
    );
  },
);

export default InquiryInfo;
